import { useCallback } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay';
import { fetchQuery } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { showErrorNotification, showWarningNotification } from '../../../widgets/notificationToaster';
import {
    ServiceAvailabilityResult,
    serviceAvailabilityStatusQuery,
    ServicesWithCredits
} from '../queries/__generated__/serviceAvailabilityStatusQuery.graphql';
import { ServiceAvailabilityStatusQuery } from '../queries/serviceAvailabilityStatus';
import { ToolType } from '../types';
import { useGenerationPreflightFirstPackMutation } from './__generated__/useGenerationPreflightFirstPackMutation.graphql';
import { useGenerationPreflightTrialStatusQuery } from './__generated__/useGenerationPreflightTrialStatusQuery.graphql';

/**
 * All AI Studio tools draw from a single shared credit pool, exposed through the
 * AI_STUDIO service. Availability (credits, plan inclusion, usage limits, ...) is
 * therefore always checked against this combined pool.
 */
const AI_STUDIO_POOL_SERVICE: ServicesWithCredits = 'AI_STUDIO';

/** Maps a non-AVAILABLE non-ADDON_NOT_ACTIVE status to the warning shown in the toast. */
function getStatusWarningMessage(status: ServiceAvailabilityResult): string | null {
    switch (status) {
        case 'TRIAL_NOT_ALLOWED':
            return T._('AI Agents Studio is not available during the trial period. Purchasing your plan to access AI Agents Studio.', 'Admin');
        case 'SHOPIFY_DEV_STORE_NOT_ALLOWED':
            return T._('AI Agents Studio is not available for Shopify development stores. Use a real store to access AI Agents Studio.', 'Admin');
        case 'USE_LIMIT_EXCEEDED':
            return T._('You have reached the usage limit for this AI Agents Studio service. Please try again later.', 'Admin');
        case 'NO_CREDITS':
            return T._('You do not have enough credits to use this AI Agents Studio service. Please add credits to continue.', 'Admin');
        case 'FEATURE_NOT_INCLUDED_IN_PLAN':
            return T._('This AI Agents Studio feature is not included in your current plan. Upgrade your plan to access it.', 'Admin');
        default:
            return null;
    }
}

export interface PreflightOptions {
    /** Fired synchronously when the AI_STUDIO addon is missing and an activation flow has been triggered. Useful to show optimistic UI. */
    onAddonActivationStarted?: () => void;
    /** Override for USE_LIMIT_EXCEEDED. If provided, suppresses the default toast (e.g. to show a fair-use dialog instead). */
    onLimitReached?: (serviceName: string) => void;
    /** Fired when the availability check could not be performed (network/server error). */
    onCheckFailed?: () => void;
    /** Fired when the availability check returned a non-AVAILABLE status that aborts generation. Receives the raw enum. */
    onUnavailable?: (status: ServiceAvailabilityResult) => void;
}

export interface UseGenerationPreflightReturn {
    /**
     * Ensures the AI_STUDIO addon is active and the shared AI Studio credit pool is available before invoking onReady.
     * If the server reports ADDON_NOT_ACTIVE, the activation dialog is opened and onReady runs only on successful activation.
     * Other non-AVAILABLE statuses surface a warning toast and abort.
     */
    runWithPreflight: (toolType: ToolType, onReady: () => void | Promise<void>, options?: PreflightOptions) => Promise<void>;
}

export function useGenerationPreflight(): UseGenerationPreflightReturn {
    const relayEnvironment = useRelayEnvironment();
    const { activateAddonForSeller, updateSubscription } = useSubscriptionData();

    const [grantFirstFreePack] = useMutation<useGenerationPreflightFirstPackMutation>(graphql`
        mutation useGenerationPreflightFirstPackMutation($input: ServiceRechargeCreditsInput!) {
            buyServiceCredits(input: $input) {
                result
                errorMessage
            }
        }
    `);

    const fetchStatus = useCallback(
        async (serviceType: ServicesWithCredits): Promise<ServiceAvailabilityResult | null> => {
            try {
                const data = await fetchQuery<serviceAvailabilityStatusQuery>(relayEnvironment, ServiceAvailabilityStatusQuery, {
                    serviceWithCredits: serviceType
                }).toPromise();
                return data?.serviceAvailabilityStatus ?? null;
            } catch (error) {
                console.error('Failed to check AI service availability:', error);
                showErrorNotification({ message: T._('Failed to check AI service availability.', 'Admin') });
                return null;
            }
        },
        [relayEnvironment]
    );

    // Returns true when the seller has never received trial credits and has never bought credits for the
    // AI Studio pool — i.e. they are entitled to the one-time free first package.
    const isEligibleForFirstFreePack = useCallback(async (): Promise<boolean> => {
        try {
            const data = await fetchQuery<useGenerationPreflightTrialStatusQuery>(
                relayEnvironment,
                graphql`
                    query useGenerationPreflightTrialStatusQuery($serviceName: ServicesWithCredits!) {
                        serviceCredit(serviceName: $serviceName) {
                            trialCreditsUsed
                            hasSellerPurchasedCredits
                        }
                    }
                `,
                { serviceName: AI_STUDIO_POOL_SERVICE },
                { fetchPolicy: 'network-only' }
            ).toPromise();
            const credit = data?.serviceCredit;
            // If serviceCredit is null the seller has no row yet, which also means they're eligible.
            if (!credit) return true;
            return !credit.trialCreditsUsed && !credit.hasSellerPurchasedCredits;
        } catch (error) {
            console.error('Failed to check AI Studio trial credit status:', error);
            return false;
        }
    }, [relayEnvironment]);

    const claimFirstFreePack = useCallback((): Promise<void> => {
        return new Promise<void>((resolve) => {
            grantFirstFreePack({
                variables: {
                    input: {
                        service: AI_STUDIO_POOL_SERVICE,
                        shopifyChargeID: null,
                        credits: 0,
                        isForTrial: true,
                        autoRefill: true,
                        currencyCode: 'USD',
                        returnUrlFromApproval: null
                    }
                },
                // The free first pack is a best-effort bonus on top of addon activation; failures
                // must not block the user (the re-run preflight will surface NO_CREDITS instead).
                onCompleted: () => resolve(),
                onError: (error) => {
                    console.warn('Failed to grant AI Studio first free credit pack:', error);
                    resolve();
                }
            });
        });
    }, [grantFirstFreePack]);

    const runWithPreflight = useCallback(
        async (toolType: ToolType, onReady: () => void | Promise<void>, options: PreflightOptions = {}) => {
            const status = await fetchStatus(AI_STUDIO_POOL_SERVICE);
            if (!status) {
                options.onCheckFailed?.();
                return;
            }

            if (status === 'AVAILABLE') {
                await onReady();
                return;
            }

            if (status === 'ADDON_NOT_ACTIVE') {
                options.onAddonActivationStarted?.();
                activateAddonForSeller(
                    'AI_STUDIO',
                    'USD',
                    async () => {
                        // Grant the one-time free first package alongside the activation. Eligibility for
                        // AI Studio itself (trial / dev store / plan) is already enforced by the preceding
                        // serviceAvailabilityStatus check: had any of those constraints applied, the status
                        // would have been TRIAL_NOT_ALLOWED / SHOPIFY_DEV_STORE_NOT_ALLOWED / FEATURE_NOT_INCLUDED_IN_PLAN
                        // and this branch would not run. We still verify the seller hasn't already consumed
                        // a trial or bought credits before so re-activation flows don't grant the pack twice.
                        if (await isEligibleForFirstFreePack()) {
                            await claimFirstFreePack();
                        }
                        // Refresh the Relay cache so subsequent checks see the activated addon and new credits.
                        updateSubscription();
                        // Re-run the preflight: any other constraint (trial, dev store, credits, etc.)
                        // could still apply even after the addon is active.
                        void runWithPreflight(toolType, onReady, options);
                    },
                    false
                );
                return;
            }

            options.onUnavailable?.(status);

            // USE_LIMIT_EXCEEDED has a custom UI hook (e.g. fair-use dialog). When provided, suppress the toast.
            if (status === 'USE_LIMIT_EXCEEDED' && options.onLimitReached) {
                options.onLimitReached(toolType);
                return;
            }

            const message = getStatusWarningMessage(status);
            if (message) showWarningNotification({ message });
        },
        [fetchStatus, activateAddonForSeller, updateSubscription, isEligibleForFirstFreePack, claimFirstFreePack]
    );

    return { runWithPreflight };
}
